import { useColorMode } from '@chakra-ui/react';
import { useEffect } from 'react';

/**
 * The class the vendored TipTap SCSS scopes its dark-mode custom properties
 * under (upstream's templates put it on `<html>`).
 */
export const VENDOR_DARK_CLASS = 'dark';

/**
 * Bridges Chakra's colour mode to the `dark` class the vendored TipTap
 * stylesheets expect.
 *
 * The vendored `*.scss` under `components/tiptap-*` declares dark values in TWO
 * shapes, across **19 files**:
 *   - a top-level `.dark { … }` sibling of `:root` (11 files). Equal
 *     specificity to `:root`, so it wins only on SOURCE ORDER — keep `.dark`
 *     last in those files when re-vendoring.
 *   - a nested `.dark &` inside a component class (9 files: button-colors,
 *     badge-colors, menu, combobox, tooltip, separator, …), which Sass compiles
 *     to `.dark .tiptap-*` (0,2,0). Wins on specificity; order-independent.
 *
 * Nothing in this app had ever applied that class — Chakra signals dark mode
 * with `data-theme="dark"` on `<html>` and `chakra-ui-dark` on `<body>` — so
 * every one of those blocks was dead code and the editor rendered light-mode
 * tokens on a dark canvas. Most were merely low-contrast; the table's
 * add-row/add-column buttons became invisible (`rgba(15,22,36,0.05)` fill
 * behind a `rgba(40,44,51,0.42)` glyph), which reads as "there is no way to add
 * a row".
 *
 * The class goes on `<html>` because TipTap's menus and the table's extend
 * buttons render through portals that don't share an ancestor inside the
 * editor.
 *
 * NOTE — there are now two dark-mode selectors in this stylesheet family, and
 * that is deliberate rather than drift: `styles/_variables.scss` bridges the
 * GLOBAL `--tt-*` vars under `.chakra-ui-dark` (a Bower fork of the upstream
 * file), while the per-component sheets stay upstream-shaped on `.dark`. Both
 * are satisfied simultaneously — Chakra puts `chakra-ui-dark` on `<body>` and
 * this bridge puts `dark` on `<html>` — so they activate together. Converging
 * them means either forking all 19 component sheets (which re-vendoring would
 * clobber) or rewriting `_variables.scss` back to `.dark`; the bridge is the
 * cheaper seam.
 *
 * AA tuning for tokens these blocks turn on lives in
 * `theme/styles/tiptap-editor.css` under `:root.dark` — several were authored
 * for a lighter dark canvas than Bower's and still failed WCAG once live.
 *
 * A JS bridge rather than 11 SCSS selector edits: those files are vendored from
 * upstream `tiptap-ui` and re-synced, so per-file edits get clobbered. Same
 * shape as `BowerMantineProvider`, which force-feeds Chakra's mode to Mantine.
 *
 * Renders nothing. Mount once, inside `ChakraProvider`.
 */
export function VendorDarkModeBridge(): null {
  const { colorMode } = useColorMode();

  useEffect(() => {
    if (colorMode !== 'dark') return;
    const root = document.documentElement;
    root.classList.add(VENDOR_DARK_CLASS);
    // Removing on cleanup (rather than only on the light branch) also unwinds
    // the class when the app unmounts, so a test that renders in dark mode
    // can't leak it onto the next one.
    return () => root.classList.remove(VENDOR_DARK_CLASS);
  }, [colorMode]);

  return null;
}
